import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useGetAdminNavMenusQuery } from '../services/menuApi'
import type { Menu } from '../types/menu'

/**
 * 메뉴 트리에서 첫 번째 접근 가능한(R 권한이 있는) 페이지 경로를 찾는다.
 * - up_menu_no 와 sort_sn 을 사용해 트리를 빌드, 깊이 우선 탐색.
 * - 자식이 있는 메뉴는 자식 먼저 탐색(자식 leaf 페이지가 더 정확).
 * - lnkg_addr 가 있고 auth_r === 'Y' 인 첫 번째 메뉴를 반환.
 * - 없으면 null.
 *
 * 로그인 직후 해당 사용자가 처음으로 볼 페이지를 결정할 때 사용.
 */
export function findFirstAccessibleMenuPath(menus: Menu[]): string | null {
  if (!menus || menus.length === 0) return null

  // 부모 키별로 그룹핑 후 sort_sn 오름차순 정렬
  const ROOT = '__root__'
  const byParent = new Map<string, Menu[]>()
  for (const m of menus) {
    const key = m.up_menu_no && m.up_menu_no.length > 0 ? m.up_menu_no : ROOT
    const arr = byParent.get(key) ?? []
    arr.push(m)
    byParent.set(key, arr)
  }
  for (const arr of byParent.values()) {
    arr.sort((a, b) => (a.sort_sn ?? 0) - (b.sort_sn ?? 0))
  }

  const dfs = (parentKey: string): string | null => {
    const children = byParent.get(parentKey) ?? []
    for (const child of children) {
      // 자식이 있으면 자식부터 탐색 (메뉴 그룹의 첫 페이지로 진입)
      const sub = dfs(child.menu_no)
      if (sub) return sub
      // leaf: 권한 OK + 링크 있음
      if (child.lnkg_addr && child.lnkg_addr.length > 0 && child.auth_r !== 'N') {
        return child.lnkg_addr
      }
    }
    return null
  }

  return dfs(ROOT)
}

export interface MenuPermission {
  canRead: boolean      // R — 읽기 (메뉴 노출 여부)
  canCreate: boolean    // C — 생성/등록
  canUpdate: boolean    // U — 수정
  canDelete: boolean    // D — 삭제
  canExport: boolean    // E — 다운로드/엑셀
  /** 현재 경로와 매칭된 메뉴(없으면 undefined) */
  matchedMenu?: Menu
}

const normalize = (p?: string) => {
  if (!p) return ''
  const trimmed = p.replace(/\/+$/, '')
  return trimmed || '/'
}

/**
 * 현재(또는 지정한) 경로와 일치하는 메뉴의 R/C/U/D/E 권한을 반환.
 *
 * 매칭 규칙:
 * 1) 정규화된 현재 경로가 메뉴의 lnkg_addr 와 정확히 일치
 * 2) 또는 현재 경로가 메뉴의 lnkg_addr 로 시작 (e.g. /instructor-recruit/edit/123 → /instructor-recruit)
 * 3) 가장 긴 prefix 매칭 우선
 *
 * 백엔드(selectAdminNavMenuList)가 권한 필터를 적용하지 않은 경우(authrt_no 미주입)
 * 모든 권한이 'Y' 로 내려오므로, 슈퍼관리자/필터 미적용 환경에서도 안전하게 동작.
 *
 * 매칭되는 메뉴가 없으면(예: 메뉴에 등록되지 않은 페이지) 모든 권한을 false 로 반환한다.
 * 호출부에서 필요한 경우 fallback 처리를 직접 한다.
 */
export function useMenuPermission(path?: string): MenuPermission {
  const location = useLocation()
  const { data } = useGetAdminNavMenusQuery()

  return useMemo(() => {
    const target = normalize(path ?? location.pathname)
    const menus: Menu[] = data?.data ?? []

    // lnkg_addr 가 있는 메뉴만 후보로 두고, 길이 내림차순으로 정렬해 가장 긴 매칭 우선.
    const candidates = menus
      .filter((m) => m.lnkg_addr && m.lnkg_addr.length > 0)
      .map((m) => ({ menu: m, addr: normalize(m.lnkg_addr) }))
      .sort((a, b) => b.addr.length - a.addr.length)

    const matched = candidates.find(
      ({ addr }) => target === addr || target.startsWith(addr + '/'),
    )?.menu

    if (!matched) {
      return {
        canRead: false,
        canCreate: false,
        canUpdate: false,
        canDelete: false,
        canExport: false,
      }
    }

    return {
      canRead: matched.auth_r === 'Y',
      canCreate: matched.auth_c === 'Y',
      canUpdate: matched.auth_u === 'Y',
      canDelete: matched.auth_d === 'Y',
      canExport: matched.auth_e === 'Y',
      matchedMenu: matched,
    }
  }, [data, location.pathname, path])
}
